import * as React from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Box,
    useTheme,
    useMediaQuery,
    Collapse,
    Divider
} from "@material-ui/core";
import TraceroLogo from "../../../assets/logos/logo.svg";
import { Link, useLocation, useRouteMatch } from "react-router-dom";
import { useAuth } from "../../../services/authentication";
import headerBarStyles from "../Styling/HeaderBarStyling";
import AccountCircle from "@material-ui/icons/AccountCircle";
import { INavigationItem, INavigationStyle } from "../../shared/Navigation";
import Dehaze from "@material-ui/icons/Dehaze";
import { ResponsiveNavigationMenu } from "../../ResponsiveNavigationMenu";
import { FullNavigationMenu } from "../../FullNavigiationMenu";
import { TRACER_VISION, FOUR_D_VISUALISATION } from "../../../assets/Constants";
import {
    ROUTE_PATH_PROJECTS,
    ROUTE_NOTIFICATIONS,
    ROUTE_PREFERENCES,
    ROUTE_PROFILE,
    ROUTE_API_ACCESS
} from "../../../routes";
import Notifications from "../../notifications/Notifications";
import ModalDialog, { useModalDialog } from "../../ModalDialog";
import UserProfileForm from "../../projectusers/UserProfileForm";
import UserPreferencesForm from "../../projectusers/UserPreferencesForm";
import UserApiAccessForm from "../../projectusers/UserApiAccessForm";

const HeaderBar: React.FC = () => {
    const projectsRouteMatch = useRouteMatch("/projects/:projectId");
    const currentLocation = useLocation();
    const classes = headerBarStyles();
    const theme = useTheme();
    const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

    const { authState, logout } = useAuth();

    const [responsiveNavOpen, setResponsiveNavOpen] = React.useState(false);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    const [projectId, setProjectId] = React.useState<string | undefined>(
        projectsRouteMatch?.params["projectId"]
    );

    React.useEffect(() => {
        setProjectId(projectsRouteMatch?.params["projectId"]);

        //// if current location changes we need to close the nav menu.
        setResponsiveNavOpen(false);
    }, [projectsRouteMatch?.params["projectId"], currentLocation]);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const scrollTo = () => {
        const elm = document.getElementById("request-demo");

        if (elm) {
            window.scrollTo(0, elm.offsetTop);
        }
    };

    const scrollToCaseStudy = () => {
        const elm = document.getElementById("case-study");

        if (elm) {
            window.scrollTo(0, elm.offsetTop);
        }
    };

    const getUserDisplayName = () => {
        return authState !== null && authState.user !== null
            ? (authState.user.displayName !== undefined
                  ? authState.user.displayName
                  : authState.user.email) +
                  (authState.user.isReservoirPortalAdministrator
                      ? " (admin)"
                      : "")
            : ".";
    };

    const getUserId = () => {
        return authState !== null && authState.user !== null
            ? authState.user.id
            : "";
    };

    const responsiveMenuClick = () => {
        setResponsiveNavOpen(!responsiveNavOpen);
    };

    const getNavigationItems = (
        includeProjects?: boolean
    ): Array<INavigationItem> | null => {
        const getCurrentPageRegEx = new RegExp("[^/]+$");

        const pathMatch = getCurrentPageRegEx.exec(currentLocation.pathname);

        const currentPageName = pathMatch != null ? pathMatch[0] : null;

        let navItems: Array<INavigationItem> = new Array<INavigationItem>();

        if (!authState.loggedIn) {
            navItems.push({
                title: "Request a demo",
                onClick: scrollTo,
                relativePath: ""
            });
            navItems.push({
                title: "Case Studies",
                onClick: scrollToCaseStudy,
                relativePath: ""
            });
            return navItems;
        }

        const isInAccountSection = currentLocation.pathname.startsWith(
            "/account"
        );

        if (isInAccountSection) {
            navItems.push(
                {
                    title: "Home",
                    relativePath: ROUTE_PATH_PROJECTS
                },
                {
                    title: "Notifications",
                    relativePath: ROUTE_NOTIFICATIONS
                },
                {
                    title: "Profile",
                    relativePath: ROUTE_PROFILE
                },
                {
                    title: "Preferences",
                    relativePath: ROUTE_PREFERENCES
                },
                {
                    title: "API Access",
                    relativePath: ROUTE_API_ACCESS
                }
            );
        }

        if (currentPageName != null && projectId != null && includeProjects) {
            navItems.push({
                title: "Home",
                relativePath: ROUTE_PATH_PROJECTS
            });
        }

        if (currentPageName != null && projectId != null) {
            navItems.push(
                {
                    title: "Project Overview",
                    relativePath: `/projects/${projectId}`
                },
                {
                    title: "Documents",
                    relativePath: `/projects/${projectId}/documents`
                },
                {
                    title: "Samples",
                    relativePath: `/projects/${projectId}/samples`
                },
                {
                    title: "Reports",
                    relativePath: "",
                    nestedList: new Array<INavigationItem>(
                        {
                            title: `${TRACER_VISION}`,
                            relativePath: `/projects/${projectId}/TracerVision`
                        },
                        {
                            title: `${FOUR_D_VISUALISATION}`,
                            relativePath: `/projects/${projectId}/4d-visualisations`
                        }
                    )
                }
            );

            if (authState?.user?.isReservoirPortalAdministrator)
                navItems.push({
                    title: "Users",
                    relativePath: `/projects/${projectId}/users`
                });
        }

        return navItems;
    };

    const getResponsiveNavItems = (): Array<INavigationItem> | null => {
        let navItems = getNavigationItems(true);

        if (navItems != null && authState.loggedIn) {
            navItems.push({
                title: "Account",
                relativePath: "",
                nestedList: new Array<INavigationItem>(
                    {
                        title: "Notifications",
                        relativePath: ROUTE_NOTIFICATIONS
                    },
                    {
                        title: "Profile",
                        onClick: toggleProfileModal,
                        relativePath: ""
                    },
                    {
                        title: "Preferences",
                        onClick: togglePreferencesModal,
                        relativePath: ""
                    },
                                        {
                        title: "API Access",
                        onClick: toggleApiAccessModal,
                        relativePath: ""
                    },
                    {
                        title: "Logout",
                        onClick: logout,
                        relativePath: ""
                    }
                )
            });
        }

        return navItems;
    };

    const responsiveNavStyle: INavigationStyle = {
        listItem: {
            fontSize: "0.9em",
            display: "flex"
        }
    };

    const navStyle: INavigationStyle = {
        listItem: {
            display: "inline !important"
        },
        list: {
            margin: "5px 0 0 0"
        }
    };

    const [profileModalOpen, , toggleProfileModal] = useModalDialog();
    const [preferencedModalOpen, , togglePreferencesModal] = useModalDialog();
    const [ApiAccessModalOpen, , toggleApiAccessModal] = useModalDialog();

    if (authState.loggedIn) {
        return (
            <div className={classes.root}>
                <AppBar position="relative">
                    <Toolbar>
                        <Box>
                            <Link to={ROUTE_PATH_PROJECTS}>
                                <img
                                    className={classes.headerLogo}
                                    src={TraceroLogo}
                                    alt="Tracerco - insight through innovation"
                                />
                            </Link>
                        </Box>
                        <Box className={classes.navigationContainer}>
                            {!mobileView ? (
                                <Box className={classes.usernameContainer}>
                                    <Box
                                        display="inline"
                                        className={classes.greeting}
                                        borderBottom={1}
                                    >
                                        <Box component="span">
                                            {getUserDisplayName()}
                                        </Box>
                                        <Box component="span">
                                            <Notifications />
                                        </Box>
                                        <Box display="inline">
                                            <IconButton
                                                onClick={handleMenu}
                                                className={classes.prefButton}
                                                color="inherit"
                                            >
                                                <AccountCircle
                                                    className={classes.userIcon}
                                                />
                                            </IconButton>
                                            <Menu
                                                id="menu-appbar"
                                                anchorEl={anchorEl}
                                                anchorOrigin={{
                                                    vertical: "bottom",
                                                    horizontal: "right"
                                                }}
                                                transformOrigin={{
                                                    vertical: "top",
                                                    horizontal: "right"
                                                }}
                                                getContentAnchorEl={null}
                                                open={openMenu}
                                                onClose={handleCloseMenu}
                                            >
                                                <MenuItem
                                                    onClick={() => {
                                                        handleCloseMenu();
                                                        toggleProfileModal();
                                                    }}
                                                >
                                                    Profile
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => {
                                                        handleCloseMenu();
                                                        togglePreferencesModal();
                                                    }}
                                                >
                                                    Preferences
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => {
                                                        handleCloseMenu();
                                                        toggleApiAccessModal();
                                                    }}
                                                >
                                                    API Access
                                                </MenuItem>
                                                <Divider />
                                                <MenuItem onClick={logout}>
                                                    Logout
                                                </MenuItem>
                                            </Menu>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <></>
                            )}
                            <Box display="block">
                                {mobileView ? (
                                    <IconButton onClick={responsiveMenuClick}>
                                        <Dehaze
                                            className={classes.dehazeIcon}
                                            fontSize="large"
                                        />
                                    </IconButton>
                                ) : (
                                    <FullNavigationMenu
                                        className={classes.navigation}
                                        classes={{
                                            listItem: navStyle.listItem,
                                            list: navStyle.list
                                        }}
                                        navigationItems={getNavigationItems(
                                            true
                                        )}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Toolbar>
                </AppBar>
                {mobileView ? (
                    <Box className={classes.responsiveMenuContainer}>
                        <Collapse in={responsiveNavOpen}>
                            <ResponsiveNavigationMenu
                                className={classes.responsiveMenuRoot}
                                classes={{
                                    listItem: responsiveNavStyle.listItem
                                }}
                                navigationItems={getResponsiveNavItems()}
                            />
                        </Collapse>
                    </Box>
                ) : (
                    <></>
                )}
                <ModalDialog
                    isActive={preferencedModalOpen}
                    title={`Update Preferences for ${getUserDisplayName()}`}
                    confirmDisabled={false}
                    confirmAction={() => {
                        togglePreferencesModal();
                    }}
                    confirmButtonText={"confirmButtonText"}
                    handleClose={() => {
                        togglePreferencesModal();
                    }}
                    preventClose={false}
                    showError={false}
                    disableDialogActions={true}
                    errorText={"errorText"}
                >
                    <UserPreferencesForm
                        userId={getUserId()}
                        onPreferencesUpdated={() => {
                            togglePreferencesModal();
                        }}
                    />
                </ModalDialog>
                <ModalDialog
                    isActive={profileModalOpen}
                    title={`Update Profile for ${getUserDisplayName()}`}
                    confirmDisabled={false}
                    confirmAction={() => {
                        toggleProfileModal();
                    }}
                    confirmButtonText={"confirmButtonText"}
                    handleClose={() => {
                        toggleProfileModal();
                    }}
                    preventClose={false}
                    showError={false}
                    disableDialogActions={true}
                    errorText={"errorText"}
                >
                    <UserProfileForm
                        userId={getUserId()}
                        onProfileUpdated={() => {
                            toggleProfileModal();
                        }}
                    />
                </ModalDialog>
                <ModalDialog
                    isActive={ApiAccessModalOpen}
                    title={`Update API Access for ${getUserDisplayName()}`}
                    confirmDisabled={false}
                    confirmAction={() => {
                        toggleApiAccessModal();
                    }}
                    confirmButtonText={"confirmButtonText"}
                    handleClose={() => {
                        toggleApiAccessModal();
                    }}
                    preventClose={false}
                    showError={false}
                    disableDialogActions={true}
                    errorText={"errorText"}
                >
                    <UserApiAccessForm
                        userId={getUserId()}
                        onPreferencesUpdated={() => {
                            toggleApiAccessModal();
                        }}
                    />
                </ModalDialog>
            </div>
        );
    } else {
        return (
            <div className={classes.root}>
                <AppBar position="relative">
                    <Toolbar>
                        <img
                            className={classes.headerLogo}
                            src={TraceroLogo}
                            alt="Tracerco - insight through innovation"
                        />
                        <Box className={classes.navigationContainer}>
                            <Box display="block">
                                {mobileView ? (
                                    <IconButton onClick={responsiveMenuClick}>
                                        <Dehaze
                                            className={classes.dehazeIcon}
                                            fontSize="large"
                                        />
                                    </IconButton>
                                ) : (
                                    <FullNavigationMenu
                                        className={classes.navigation}
                                        classes={{
                                            listItem: navStyle.listItem,
                                            list: navStyle.list
                                        }}
                                        navigationItems={getNavigationItems()}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Toolbar>
                </AppBar>
                {mobileView ? (
                    <Box className={classes.responsiveMenuContainer}>
                        <Collapse in={responsiveNavOpen}>
                            <ResponsiveNavigationMenu
                                className={classes.responsiveMenuRoot}
                                classes={{
                                    listItem: responsiveNavStyle.listItem
                                }}
                                navigationItems={getResponsiveNavItems()}
                            />
                        </Collapse>
                    </Box>
                ) : (
                    <></>
                )}
            </div>
        );
    }
};

export default HeaderBar;
